import { getBookingSummaryTitle } from '@components/BookingFlow/Common/getBookingSummaryTitle';
import { NavioContactChatWidget } from '@components/BookingFlow/Common/NavioContactChatWidget';
import { StationaryBookingNextSteps } from '@components/BookingFlow/Stationary/StationaryBookingNextSteps';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { FlowBackButton } from '@components/Layouts/FlowBackButton';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { CustomerDataQualityProvider } from '@components/UserGroupDataQuality/CustomerDataQualityProvider';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { FixedBottomBarContainer } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { DepartmentOrganizationName, useCancelHoldOnExpiry } from 'noddi-web';
import { type PropsWithChildren, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useCustomerDepartmentSelection } from '../../hooks/useCustomerDepartmentSelection';
import { useStationaryProposalCheckout } from '../../hooks/useStationaryProposalCheckout';
import { useTrackProposalStep } from '../../hooks/useTrackProposalStep';
import {
  useBookingProposalItems,
  useBookingProposalSlug,
  useBookingProposalStationaryReservation,
  useBookingProposalUserGroupId
} from '../../stores/bookingProposalStore';
import { showsSuggestedProposalTimes } from '../../utils/stationaryProposalPolicy';
import { getStationaryProposalBackPath, getStationaryProposalFirstStepPath } from '../../utils/stationaryProposalSteps';
import { ConfirmStationaryProposalButton } from './ConfirmStationaryProposalButton';
import { StationaryProposalCheckoutBody } from './StationaryProposalCheckoutBody';

/** Same shell as the loaded summary, so loading / error keep title + back button. */
const SummaryChrome = ({
  slug,
  usesSuggestedTimes,
  children
}: PropsWithChildren<{ slug: string; usesSuggestedTimes: boolean }>) => (
  <ContentWrapperCustomerApp
    title={getBookingSummaryTitle()}
    className='max-w-screen-md'
    backButtonPath={getStationaryProposalBackPath({
      step: 'summary',
      slug,
      usesSuggestedTimes
    })}
  >
    {children}
  </ContentWrapperCustomerApp>
);

export const StationaryProposalSummary = () => {
  const { getSelectedDepartmentForGroup } = useCustomerDepartmentSelection();
  const [savedOrganizationNumber, setSavedOrganizationNumber] = useState(false);
  const slug = useBookingProposalSlug();
  const bookingProposalItems = useBookingProposalItems();
  const stationaryReservation = useBookingProposalStationaryReservation();
  const proposalUserGroupId = useBookingProposalUserGroupId();

  const {
    data: proposal,
    isPending: isProposalPending,
    error: proposalError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalBySlug,
    input: { slug }
  });

  const checkout = useStationaryProposalCheckout(proposal);
  const isReservationExpired = useCancelHoldOnExpiry(checkout.stationaryReservation);
  const promptUserGroupId = checkout.bookingUserGroupId ?? proposalUserGroupId;
  const selectedDepartment = getSelectedDepartmentForGroup(promptUserGroupId);
  const departmentName = proposal?.customerDepartmentName || selectedDepartment?.name;
  const organizationName = proposal?.customerOrganizationName || checkout.organizationName;
  const usesSuggestedTimes = proposal != null && showsSuggestedProposalTimes(proposal.serviceDepartment.brand.domain);

  // No hold, or a login still owed, means this page redirects instead of rendering.
  useTrackProposalStep('summary', !!stationaryReservation && checkout.isLoggedIn);

  if (!stationaryReservation) {
    return <Navigate to={getStationaryProposalFirstStepPath(slug, { usesSuggestedTimes })} replace />;
  }

  // Login is its own step, so this page is review-and-pay only.
  if (!checkout.isLoggedIn) {
    return <Navigate to={customerRoutes.stationaryProposalLogin.getPath({ slug })} replace />;
  }

  // Keep title / back button mounted while data settles, like the booking flow.
  if (isProposalPending || checkout.isPaymentOptionsLoading || checkout.isShoppingCartPending) {
    return (
      <SummaryChrome slug={slug} usesSuggestedTimes={usesSuggestedTimes}>
        <NoddiLoadingScreenWithTrans />
      </SummaryChrome>
    );
  }

  const summaryError = proposalError ?? checkout.shoppingCartError;
  if (summaryError) {
    return (
      <SummaryChrome slug={slug} usesSuggestedTimes={usesSuggestedTimes}>
        <ApiErrorMessageWithTrans error={summaryError} />
      </SummaryChrome>
    );
  }

  // Without payment options checkout silently defaults to pay-before, even for invoice customers.
  if (checkout.paymentOptionsError) {
    return (
      <SummaryChrome slug={slug} usesSuggestedTimes={usesSuggestedTimes}>
        <ApiErrorMessageWithTrans
          error={checkout.paymentOptionsError}
          isRetrying={checkout.isPaymentOptionsFetching}
          onRetry={() => void checkout.refetchPaymentOptions()}
        />
      </SummaryChrome>
    );
  }

  if (!proposal) {
    return null;
  }

  return (
    <CustomerDataQualityProvider
      userGroupId={promptUserGroupId}
      bookingLocationType={proposal.bookingLocationType ?? undefined}
      countryCode={checkout.addressCountryCode}
    >
      <ContentWrapperCustomerApp title={getBookingSummaryTitle()} className='max-w-screen-md' hideBackButton>
        <div className='mb-24 md:mb-0'>
          <StationaryProposalCheckoutBody
            slug={slug}
            checkout={checkout}
            onOrganizationNumberSaved={() => setSavedOrganizationNumber(true)}
            beforeChrome={
              (checkout.isOrganization || !!proposal.customerOrganizationName) && (
                <p className='flex items-start gap-1.5 text-sm text-muted-foreground'>
                  <NoddiIcon name='Building2' size='small' />
                  <span>
                    <Trans>On behalf of</Trans>
                  </span>
                  {departmentName ? (
                    <DepartmentOrganizationName departmentName={departmentName} organizationName={organizationName} />
                  ) : (
                    organizationName
                  )}
                </p>
              )
            }
            afterSummary={
              <StationaryBookingNextSteps
                serviceDepartmentId={proposal.serviceDepartment.id}
                licensePlate={bookingProposalItems[0]?.car.licensePlate.number}
                organizationName={
                  checkout.isOrganization || proposal.customerOrganizationName ? organizationName : undefined
                }
                locationName={checkout.locationName}
                confirmationPhoneNumber={checkout.customerPhoneNumber ?? null}
              />
            }
          />
        </div>

        <FixedBottomBarContainer>
          <div className='flex items-center gap-2'>
            <FlowBackButton backPath={getStationaryProposalBackPath({ step: 'summary', slug, usesSuggestedTimes })} />
            <NavioContactChatWidget />
          </div>
          <ConfirmStationaryProposalButton
            proposal={proposal}
            paymentOption={checkout.paymentOption}
            userGroupPayerId={checkout.invoiceToUserGroupId}
            totalAmount={checkout.totalAmount}
            hasValidPhone={checkout.hasValidContactPhone}
            hasOrganizationNumber={
              !checkout.isInvoice || !checkout.requiresOrganizationNumber || savedOrganizationNumber
            }
            bookingUserGroupId={checkout.bookingUserGroupId}
            isReservationExpired={isReservationExpired}
          />
        </FixedBottomBarContainer>
      </ContentWrapperCustomerApp>
    </CustomerDataQualityProvider>
  );
};
